import React, { useMemo } from 'react';

// ── Pip positions for each die face (percentages) ───────────────────────────
const PIP_LAYOUTS: Record<number, [number, number][]> = {
  1: [[50, 50]],
  2: [[30, 30], [70, 70]],
  3: [[30, 30], [50, 50], [70, 70]],
  4: [[30, 30], [70, 30], [30, 70], [70, 70]],
  5: [[30, 30], [70, 30], [50, 50], [30, 70], [70, 70]],
  6: [[30, 25], [70, 25], [30, 50], [70, 50], [30, 75], [70, 75]],
};

// ── Single floating die SVG ─────────────────────────────────────────────────
interface FloatingDieProps {
  value: number;
  size: number;
  x: number;
  y: number;
  delay: number;
  duration: number;
  rotation: number;
  opacity: number;
  direction: 'up' | 'down';
}

const FloatingDie: React.FC<FloatingDieProps> = ({
  value,
  size,
  x,
  y,
  delay,
  duration,
  rotation,
  opacity,
  direction,
}) => {
  const pips = PIP_LAYOUTS[value] || PIP_LAYOUTS[1];
  const animName = direction === 'up' ? 'dice-float-up' : 'dice-float-down';

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: size,
        height: size,
        opacity,
        transform: `rotate(${rotation}deg)`,
        animation: `${animName} ${duration}s linear ${delay}s infinite, dice-spin ${duration * 0.8}s ease-in-out ${delay}s infinite alternate`,
      }}
    >
      <svg width={size} height={size} viewBox="0 0 40 40" style={{ imageRendering: 'pixelated' }}>
        {/* Die body */}
        <rect
          x="2" y="2" width="36" height="36" rx="4"
          fill="none"
          stroke="var(--theme-primary)"
          strokeWidth="2.5"
          opacity="0.8"
        />
        {/* Inner highlight */}
        <rect
          x="4" y="4" width="32" height="32" rx="3"
          fill="var(--theme-primary)"
          opacity="0.12"
        />
        {/* Pips */}
        {pips.map(([px, py], i) => (
          <circle
            key={i}
            cx={(px / 100) * 36 + 2}
            cy={(py / 100) * 36 + 2}
            r={value === 1 ? 4 : 3}
            fill="var(--theme-primary)"
            opacity="0.7"
          />
        ))}
      </svg>
    </div>
  );
};

// ── Background component ────────────────────────────────────────────────────
interface DiceBackgroundProps {
  count?: number;
  className?: string;
}

const DiceBackground: React.FC<DiceBackgroundProps> = ({ count = 18, className = '' }) => {
  // Generate dice positions deterministically so they don't shift on re-render
  const dice = useMemo(() => {
    const items: FloatingDieProps[] = [];
    // Use a simple seeded pseudo-random for consistent layout
    let seed = 42;
    const rand = () => {
      seed = (seed * 16807 + 0) % 2147483647;
      return (seed - 1) / 2147483646;
    };

    for (let i = 0; i < count; i++) {
      items.push({
        value: Math.floor(rand() * 6) + 1,
        size: 20 + Math.floor(rand() * 24), // 20-44px
        x: rand() * 95,
        y: rand() * 100,
        delay: -(rand() * 30), // negative = already in progress
        duration: 18 + rand() * 22, // 18-40s
        rotation: Math.floor(rand() * 360),
        opacity: 0.08 + rand() * 0.14, // 0.08-0.22 -- visible but not distracting
        direction: rand() > 0.5 ? 'up' : 'down',
      });
    }
    return items;
  }, [count]);

  return (
    <div className={`absolute inset-0 overflow-hidden pointer-events-none z-0 ${className}`}>
      {dice.map((die, i) => (
        <FloatingDie key={i} {...die} />
      ))}
    </div>
  );
};

export default DiceBackground;
